import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Package, Clock, CheckCircle, XCircle, Truck, Eye, Calendar } from 'lucide-react';
import api from '../services/api';

// Image URL helper function - HTTPS uyumlu
const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  
  // Production'da HTTPS kullan
  if (process.env.NODE_ENV === 'production') {
    // HTTPS ile başlamıyorsa HTTPS ekle
    if (imagePath.startsWith('http://')) {
      return imagePath.replace('http://', 'https://');
    }
    // Relative path ise HTTPS domain ile birleştir
    if (imagePath.startsWith('/')) {
      return `https://leasertech.com${imagePath}`;
    }
    return `https://leasertech.com/${imagePath}`;
  }
  
  // Development'ta localhost kullan
  const API_BASE_URL = 'http://localhost:5000';
  return `${API_BASE_URL}${imagePath}`;
};

const Orders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    if (user) {
      loadOrders();
    }
  }, [user]);

  const loadOrders = async () => {
    try {
      const response = await api.get('/orders');
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Siparişler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'beklemede': return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'onaylandi': return <Package className="h-5 w-5 text-blue-500" />;
      case 'hazirlaniyor': return <Package className="h-5 w-5 text-purple-500" />;
      case 'kargoda': return <Truck className="h-5 w-5 text-indigo-500" />;
      case 'teslim_edildi': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'iptal': return <XCircle className="h-5 w-5 text-red-500" />;
      // Eski değerler için backward compatibility
      case 'pending': return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'processing': return <Package className="h-5 w-5 text-blue-500" />;
      case 'shipped': return <Truck className="h-5 w-5 text-purple-500" />;
      case 'delivered': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'cancelled': return <XCircle className="h-5 w-5 text-red-500" />;
      default: return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'beklemede': return 'Beklemede';
      case 'onaylandi': return 'Onaylandı';
      case 'hazirlaniyor': return 'Hazırlanıyor';
      case 'kargoda': return 'Kargoda';
      case 'teslim_edildi': return 'Teslim Edildi';
      case 'iptal': return 'İptal Edildi';
      // Eski değerler için backward compatibility
      case 'pending': return 'Beklemede';
      case 'processing': return 'Hazırlanıyor';
      case 'shipped': return 'Kargoda';
      case 'delivered': return 'Teslim Edildi';
      case 'cancelled': return 'İptal Edildi';
      default: return status || 'Bilinmiyor';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'beklemede': return 'bg-yellow-100 text-yellow-800';
      case 'onaylandi': return 'bg-blue-100 text-blue-800';
      case 'hazirlaniyor': return 'bg-purple-100 text-purple-800';
      case 'kargoda': return 'bg-indigo-100 text-indigo-800';
      case 'teslim_edildi': return 'bg-green-100 text-green-800';
      case 'iptal': return 'bg-red-100 text-red-800';
      // Eski değerler için backward compatibility
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'shipped': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Tarih bilgisi yok';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Geçersiz tarih';
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Tarih hatası';
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined || isNaN(price)) return '₺0,00';
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return '₺0,00';
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numPrice);
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Siparişler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Siparişlerim</h1>
              <p className="text-gray-600 mt-1">Tüm siparişlerinizi buradan takip edebilirsiniz</p>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Package className="h-5 w-5" />
              <span>{orders.length} sipariş</span>
            </div>
          </div>
        </div>

        {/* Orders List */}
        {orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz siparişiniz yok</h3>
            <p className="text-gray-600 mb-6">İlk siparişinizi vermek için ürünlerimizi inceleyin</p>
            <a
              href="/products"
              className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Alışverişe Başla
            </a>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                {/* Order Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(order.order_status || order.status)}
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.order_status || order.status)}`}>
                          {getStatusText(order.order_status || order.status)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">
                        <span className="flex items-center space-x-1">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDate(order.created_at || order.createdAt || order.created_at)}</span>
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="text-lg font-semibold text-gray-900">
                          {formatPrice(order.total || order.total_amount || 0)}
                        </p>
                        <p className="text-sm text-gray-500">
                          Sipariş #{order.order_number || order.id || '-'}
                        </p>
                      </div>
                      <button
                        onClick={() => setSelectedOrder(selectedOrder?.id === order.id ? null : order)}
                        className="flex items-center space-x-2 px-4 py-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                        <span>Detaylar</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {order.items?.map((item, index) => (
                      <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <img
                          src={getImageUrl(
                            typeof item.product?.images?.[0] === 'object' 
                              ? item.product?.images?.[0]?.url 
                              : item.product?.images?.[0] || item.image || '/images/placeholder.jpg'
                          )}
                          alt={item.product?.name || item.name}
                          className="w-12 h-12 object-contain rounded-lg border"
                          crossOrigin="anonymous"
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkdyw7xzw7xsIFlva8SxPC90ZXh0Pjwvc3ZnPg==';
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {item.product?.name || item.name || 'Ürün'}
                          </p>
                          <p className="text-sm text-gray-500">
                            Adet: {item.quantity || 1}
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {formatPrice((item.price || 0) * (item.quantity || 1))}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Order Details */}
                {selectedOrder?.id === order.id && (
                  <div className="border-t border-gray-200 p-6 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Shipping Address */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Teslimat Adresi</h4>
                        <div className="text-sm text-gray-600 space-y-1">
                          <p className="font-semibold">{order.shipping_name || order.shippingAddress?.firstName + ' ' + order.shippingAddress?.lastName || '-'}</p>
                          <p>{order.shipping_address || order.shippingAddress?.address || '-'}</p>
                          <p>
                            {order.shipping_city || order.shippingAddress?.city || ''}
                            {(order.shipping_state || order.shippingAddress?.state) ? `, ${order.shipping_state || order.shippingAddress?.state}` : ''}
                          </p>
                          {(order.shipping_zip_code || order.shippingAddress?.zipCode) && (
                            <p>Posta Kodu: {order.shipping_zip_code || order.shippingAddress?.zipCode}</p>
                          )}
                          <p>Telefon: {order.shipping_phone || order.shippingAddress?.phone || order.contactInfo?.phone || '-'}</p>
                          {(order.guest_email || order.contactInfo?.email) && (
                            <p>E-posta: {order.guest_email || order.contactInfo?.email}</p>
                          )}
                        </div>
                      </div>

                      {/* Order Summary */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Sipariş Özeti</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Ürünler:</span>
                            <span className="font-medium">{formatPrice(order.subtotal || order.subtotal_amount || 0)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Kargo:</span>
                            <span className="font-medium">
                              {(order.shipping_cost || order.shippingCost || 0) > 0 
                                ? formatPrice(order.shipping_cost || order.shippingCost || 0) 
                                : 'Ücretsiz'}
                            </span>
                          </div>
                          <div className="flex justify-between text-base font-semibold border-t border-gray-200 pt-2">
                            <span>Toplam:</span>
                            <span>{formatPrice(order.total || order.total_amount || 0)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Order Notes */}
                    {order.notes && (
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Notlar</h4>
                        <p className="text-sm text-gray-600 bg-white p-3 rounded-lg border">
                          {order.notes}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;